import React from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaLoader, { KuikaLoaderSize, KuikaLoaderVariant } from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { noop } from "../_layout/utils";
import { VariantGrid } from "../_layout/variant-grid";

const VARIANTS: KuikaLoaderVariant[] = ["ring", "dots", "logo"];
const SIZES: KuikaLoaderSize[] = ["sm", "md", "lg"];

interface IInkProps {
  color: string;
  children: React.ReactNode;
}

/** Sets the text colour the loader inherits. */
const Ink: React.FC<IInkProps> = ({ color, children }: IInkProps): JSX.Element => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color }}>{children}</span>
);

export const LoaderStory: React.FC = (): JSX.Element => (
  <ShowcaseFrame
    title="Loader"
    componentName="KuikaLoader"
    sourcePath="ui-kit/kuika-loader"
    description="The small loading indicator: ring (default) inside a control or row, dots inside a line of text, logo where the Kuika logo spinner stood. Colour is currentColor. Under reduced motion nothing turns or lifts — the ring and the logo breathe, the dots only brighten. Full-screen and wrapper spinners stay KuikaSpinner / KuikaSpinnerSmall."
  >
    <VariantGrid
      label="variant × size"
      caption="ring and dots on the icon ladder (14 / 16 / 18); logo on the control-height ladder (24 / 32 / 40)"
      rows={VARIANTS}
      cols={SIZES}
      render={(variant: string, size: string): React.ReactNode => (
        <KuikaLoader variant={variant as KuikaLoaderVariant} size={size as KuikaLoaderSize} />
      )}
    />
    <SpecRow label="Colour" caption="currentColor — the loader takes the text colour around it" inline>
      <Ink color="var(--blue-5)">
        <KuikaLoader />
        <KuikaLoader variant="dots" />
      </Ink>
      <Ink color="var(--red-5)">
        <KuikaLoader />
        <KuikaLoader variant="dots" />
      </Ink>
      <Ink color="var(--dark-7)">
        <KuikaLoader />
        <KuikaLoader variant="dots" />
      </Ink>
    </SpecRow>
    <SpecRow label="Dots in text" caption="sits on the text baseline" inline>
      <span style={{ color: "var(--dark-9)" }}>
        Loading models <KuikaLoader variant="dots" size="sm" label="Loading models" />
      </span>
      <span style={{ color: "var(--dark-9)" }}>
        Fetching schema <KuikaLoader variant="dots" />
      </span>
    </SpecRow>
    <SpecRow
      label="Button loading"
      caption=".kuika-button__spinner is the same ring, sized to the button's icon slot"
      inline
    >
      <KuikaButton type="primary" size="sm" loading onClick={noop}>
        Save
      </KuikaButton>
      <KuikaButton type="primary" loading onClick={noop}>
        Save
      </KuikaButton>
      <KuikaButton type="secondary" size="lg" loading onClick={noop}>
        Publish
      </KuikaButton>
      <KuikaButton type="secondary" color="danger" loading onClick={noop}>
        Delete
      </KuikaButton>
    </SpecRow>
    <SpecRow label="KuikaSmallSpinner" caption="the ring under its old name, in brand blue" inline>
      <KuikaSmallSpinner />
      <Ink color="var(--dark-9)">
        <KuikaSmallSpinner />
        Switching branch
      </Ink>
    </SpecRow>
    <SpecRow label="decorative" caption="inside an owner that already announces the wait: aria-hidden, no role" inline>
      <Ink color="var(--dark-9)">
        <KuikaLoader decorative />
        Saving (the status line says so)
      </Ink>
    </SpecRow>
  </ShowcaseFrame>
);
